import { useAnalytics } from '@oriola-corporation/kronan-analytics';
import { Cross } from '@oriola-corporation/kronan-icons';
import React, { FC, ButtonHTMLAttributes, useMemo } from 'react';
import { cn } from '../../utils';
import { BoxReset } from '../Box/BoxReset';
import { ButtonProps, Button, IconButton } from '../Button';
import { AnalyticsEvents, MODAL_BUTTON_CLICK } from './analytics';

export type ModalAction = {
  label: string;
} & Omit<ButtonProps, 'children'> &
  ButtonHTMLAttributes<HTMLButtonElement>;

export interface ModalContentProps extends React.PropsWithChildren {
  title: string;
  closeLabel?: string;
  onClose: () => void;
  className?: string;
  titleIcon?: {
    component: React.ReactNode;
  };
  actions?: Array<ModalAction | undefined | false | null>;
  subtitle?: string;
  showCloseButton?: boolean;
  isWithoutContentPadding?: boolean;
  id: string;
}

const isModalAction = (action: ModalAction | undefined | false | null): action is ModalAction => {
  if (!action) return false;
  return true;
};

const compareActions = (a: ModalAction, b: ModalAction) => {
  const order = { primary: 0, secondary: 1, tertiary: 2 } as const;
  return order[a.variant || 'primary'] - order[b.variant || 'primary'];
};

export const ModalContent: FC<ModalContentProps> = ({
  title,
  closeLabel = 'Stäng',
  onClose,
  children,
  titleIcon,
  actions = [],
  subtitle,
  showCloseButton,
  isWithoutContentPadding,
  id,
}) => {
  const { trackEvent } = useAnalytics<AnalyticsEvents>();

  const sortedActions = useMemo(() => {
    return actions.filter(isModalAction).sort(compareActions);
  }, [actions]);

  //This is needed for focus management in chrome desktop
  const callBackRef = React.useCallback((node: HTMLHeadingElement) => {
    setTimeout(() => {
      node?.focus();
    }, 300);
  }, []);

  return (
    <div id="dialog-content" className="flex size-full min-h-fit flex-col pb-4 md:pb-6">
      <div
        className={cn(
          'sticky top-0 z-10 ml-4 justify-between bg-white pb-2 md:ml-6',
          showCloseButton ? 'pt-5' : 'pt-4'
        )}
      >
        <div className="mr-12 flex items-center gap-2">
          {titleIcon?.component}
          <h1
            ref={callBackRef}
            tabIndex={-1}
            id={`${id}-title`}
            className={cn('headline-s text-start focus:outline-none focus-visible:outline-none')}
          >
            {title}
          </h1>
        </div>
        {subtitle && <p>{subtitle}</p>}

        {showCloseButton && (
          <IconButton
            variant="secondary"
            className="!absolute right-2 top-2"
            onClick={() => onClose()}
            icon={<Cross />}
            aria-label={closeLabel}
          />
        )}
      </div>
      <BoxReset variant="default" level={1}>
        {isWithoutContentPadding ? (
          <>{children}</>
        ) : (
          <div className="min-h-0 grow overflow-y-auto px-4 py-1 md:px-6">{children}</div>
        )}
      </BoxReset>
      {sortedActions.length > 0 && (
        <div className="mt-5 flex flex-col flex-wrap gap-3 px-4 sm:flex-row md:px-6">
          {sortedActions.map((action, index) => {
            const { variant = 'primary', label, onClick, className, ...rest } = action;

            return (
              <Button
                key={index}
                variant={variant}
                className={cn('grow-0', className)}
                onClick={(e) => {
                  trackEvent({
                    type: MODAL_BUTTON_CLICK,
                    payload: {
                      title,
                      actionLabel: (e.target as HTMLElement).innerHTML,
                    },
                  });
                  if (onClick) {
                    onClick(e);
                  }
                }}
                {...rest}
              >
                {action.label}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
};
